import { useContext, useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useParams } from "react-router-dom";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { UserContext } from "../context/UserContext";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import { Error } from "../models/error";
import { Filters, Searches, Sort, Sorts } from "../models/shipeezi.table";
import { Timezone } from "../models/timezone";
import { User } from "../models/user";
import useRoutePlannerActions from "../pages/private/Shared/Settings/People/Users/hooks/useRoutePlannerActions";
import TimezoneService from "../services/timezone.service";
import UserService from "../services/user.service";
import { checkError } from "../util/checkError";
import { Routes, Status, UserTypes } from "../util/enums";
import getRelatedFilters from "../util/getRelatedFilters";
import { UserKeys } from "../util/queries";
import useActionAutomaticSequence from "./useActionAutomaticSequence";
import useActionSyncUser from "./useSyncUser";

export default function useUserContext(history?: any, isOnProfilePage?: boolean) {
  const { userId } = useParams<{ userId?: string | undefined }>();
  const { currentUser, setHasUserChanged, warehouse } = useAppState();
  const { t: translate } = useTranslation();
  const { setUsers, handleUpdateUser, ...rest } = useContext(UserContext);
  const { mutate: syncUser } = useActionSyncUser();
  const { isAutomaticSequenceReference, increaseAutomaticSequence } = useActionAutomaticSequence();

  const { useUpdateToRoutePlanner } = useRoutePlannerActions();
  const { mutate: updateInRoutePlanner } = useUpdateToRoutePlanner();

  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const [sequence, setSequence] = useState<AutomaticSequencing>();

  const [timezones, setTimezones] = useState<Timezone[]>([]);

  const [errorResponse, setErrorResponse] = useState<Error>();

  const queryClient = useQueryClient();

  const { refetch: refetchUserList, isFetching } = useQuery({
    queryKey: UserKeys.getAll([page, pageSize, filters, searches, sorting]),
    queryFn: () =>
      UserService.getAll({
        page,
        pageSize,
        isAdmin: currentUser?.isAdmin ? [0, 1] : false,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
          : JSON.stringify([["updatedAt", "desc"]]),
        include: JSON.stringify([
          { association: "role" },
          { association: "location" },
          {
            association: "usersCompanies",
            required: true,
            include: [{ association: "role" }],
            where: {
              companyId: currentUser?.companyId,
              ...getRelatedFilters(filters, "usersCompanies"),
            },
          },
        ]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...getRelatedFilters(filters, "user"),
      }),
    enabled: !!currentUser?.id && window.location.href.includes("settings"),
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) {
        if (setUsers) {
          setUsers(
            response.data.map((user) => ({
              ...user,
              userCompany:
                user.usersCompanies && user.usersCompanies.length > 0
                  ? user.usersCompanies[0]
                  : undefined,
            }))
          );
        }
      }
    },
  });

  const { refetch: refetchUsersCount } = useQuery({
    queryKey: UserKeys.count([filters, searches]),
    queryFn: () =>
      UserService.count({
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        isAdmin: false,
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            where: {
              companyId: currentUser?.companyId,
              ...getRelatedFilters(filters, "usersCompanies"),
            },
          },
        ]),
        ...getRelatedFilters(filters, "user"),
      }),
    enabled: !!currentUser?.id && window.location.href.includes(Routes.SETTINGS),
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setTotal(response.data);
      else setTotal(0);
    },
  });

  const { refetch: refetchUser, isFetching: fetchingUser } = useQuery(
    ["user.id"],
    () =>
      UserService.getById(!isOnProfilePage ? Number(userId) : Number(currentUser?.id), {
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            include: [
              { association: "manager" },
              {
                association: "company",
                include: [{ association: "location" }, { association: "profile" }],
              },
              { association: "offices" },
              { association: "measurement" },
              { association: "measurementWeight" },
              { association: "applications" },
            ],
            where: { companyId: currentUser?.companyId },
          },
          { association: "timezone" },
          { association: "role" },
          { association: "location" },
          { association: "translate" },
          { association: "picture" },
          {
            required: false,
            association: "branches",
            where: { companyId: currentUser?.companyId },
          },
          {
            required: false,
            association: "projects",
            through: {
              where: {
                companyId: currentUser?.companyId,
              },
            },
          },
          { association: "shiftTimes" },
          { association: "fleet", attributes: ["id", "externalUuid", "fleetNumber", "model"] },
        ]),
      }),
    {
      enabled:
        !window.location.href.includes(Routes.SETTINGS) &&
        !window.location.href.includes(Routes.PROFILE)
          ? !!userId
          : !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          if (handleUpdateUser) {
            let mobilePhone = response.data.phone;
            const userCompany = response?.data?.usersCompanies?.[0];

            let data: User = { ...response.data };

            if (mobilePhone) {
              if (
                mobilePhone.length > 0 &&
                !mobilePhone.includes("+") &&
                !isValidPhoneNumber(mobilePhone)
              )
                mobilePhone = `+${mobilePhone}`;
            }

            if (userCompany) {
              delete userCompany?.id;
              delete userCompany?.companyId;

              data = {
                ...data,
                ...userCompany,
              };
            }

            handleUpdateUser({
              ...data,
              phone: mobilePhone,
            });
          }
        }
      },
    }
  );

  const { mutate: updateUser, isLoading: isUpdating } = useMutation({
    mutationKey: UserKeys.update(),
    mutationFn: ({ id, user }: { id: number; user: User }) => UserService.update(id, user),
    onSuccess: (_: AxiosResponse<boolean>, variables: { id: number; user: User }) => {
      const includeUserCompanies = {
        association: "usersCompanies",
        required: true,
        where: {
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          typeUser: [UserTypes.ADMIN, UserTypes.MOVING_WAREHOUSE, UserTypes.OPERATIONS],
        },
      };

      const warehouseBranchesId = warehouse?.branches?.map((branch) => branch.id);

      const includeBranches = {
        association: "branches",
        required: true,
        where: {
          id: warehouseBranchesId,
        },
      };

      if (variables?.id === currentUser?.id) syncUser();

      if (!isOnProfilePage && !userId) {
        refetchUserList();
        refetchUsersCount();
        setHasErrors(false);
      } else if (userId) {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        handleGoBackToPeopleTab();
      } else if (isOnProfilePage) {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        syncUser();
        refetchUser();
      }

      if (currentUser?.company.useRoutePlanner && variables?.user?.externalUuid) {
        updateInRoutePlanner({ id: variables?.user?.externalUuid, user: variables.user });
      }

      queryClient.invalidateQueries({
        queryKey: UserKeys.getAll([currentUser?.companyId, includeUserCompanies, includeBranches]),
      });
    },
    onError: (err: AxiosError<Error>) => {
      if (err && err.response) {
        if (err.response.data.field !== "unknown") {
          setErrorResponse(err.response.data);
        }
      }

      checkError(err, translate("anErrorOccurredTryAgain"));
      setHasErrors(true);
    },
  });

  const { mutate: createUser, isLoading: isCreating } = useMutation(
    ["user.create"],
    (newUser: User) => UserService.create(newUser),
    {
      onSuccess: () => {
        setHasErrors(false);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        handleGoBackToPeopleTab();
      },
      onError: (err: AxiosError<Error>) => {
        checkError(err, translate("anErrorOccurredTryAgain"));
        setHasErrors(true);
      },
    }
  );

  const { mutate: onSendResetPassword, isLoading: loadingResetPassword } = useMutation(
    ["users.password.reset"],
    ({ email }: { email: string }) => UserService.forgotPassword(email),
    {
      onSuccess: (response: AxiosResponse<{ success: boolean; message?: string } | boolean>) => {
        if (response?.data && typeof response.data === "object") {
          if (!response.data?.["success"]) {
            NotificationToast.error(response?.data?.["message"] || translate("couldNotSendEmail"));
          } else {
            NotificationToast.success(translate("emailToResetPasswordSent"));
          }
        } else if (response?.data) {
          NotificationToast.success(translate("emailToResetPasswordSent"));
        } else {
          NotificationToast.error(translate("couldNotSendEmail"));
        }
      },
    }
  );

  useQuery(
    ["timezone.list"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }),
    {
      enabled:
        window.location.href.includes(Routes.USERS) ||
        window.location.href.includes(Routes.PROFILE),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTimezones(response.data);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));

    setSorting(sorting);
  };

  const handleGoBackToPeopleTab = () => {
    history?.push({
      pathname: PRIVATE_ROUTES.SETTINGS,
      state: {
        tab: translate("people"),
        tabPeople: translate("users"),
      },
    });
  };

  const handleOriginalUserBeenChanged = (originalUser?: User, newUser?: User): boolean => {
    if (originalUser && newUser) {
      const usersIsEqual = isEqual(originalUser, newUser);

      if (usersIsEqual) {
        NotificationToast.warning(translate("noChangeHasBeenDetected"));

        return false;
      }

      return true;
    }

    return false;
  };

  const handleUpdate = (newUser: User) => {
    if (handleOriginalUserBeenChanged(rest.user, newUser)) {
      updateUser({
        id: newUser?.id,
        user: { ...newUser },
      });
      if (newUser?.id === currentUser?.id) setHasUserChanged(true);
    }
  };

  const handleCreate = (newUser: User) => {
    if (newUser) {
      createUser({ ...newUser, companyId: Number(currentUser?.companyId) });
      if (newUser?.code && sequence && sequence?.id && isAutomaticSequenceReference(sequence, newUser?.code)) {
        increaseAutomaticSequence({
          sequence,
          value: newUser?.code,
        });
      }
    }
  };

  return {
    rest,
    handleUpdateUser,
    isOnProfilePage,
    fetchingUser,
    refetchUserList,
    table: {
      total,
      page,
      pageSize,
      isFetching,
      hasErrors,
      setHasErrors,
      setCurrentPage,
      setPageSize,
      handleSearch,
      handleFilter,
      handleSort,
    },
    maintenance: {
      onSendResetPassword,
      loadingResetPassword,
      handleGoBackToPeopleTab,
      sequence,
      setSequence,
      errorResponse,
    },
    timezones,
    isUpdating,
    isCreating,
    createUser,
    updateUser,
    handleUpdate,
    handleCreate,
  };
}
